import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Award, Download, FileText, Calendar, Shield, ArrowRightLeft, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { licenseApi, extractApiError } from '../../lib/api';
import api from '../../lib/api';

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        let d;
        if (dateStr.includes('.')) {
            const parts = dateStr.split('.');
            if (parts.length === 3) d = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        } else {
            d = new Date(dateStr + 'T00:00:00');
        }
        if (!d || isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
}

export function LicenseCard({ license, renewal = null }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            // Single endpoint handles both Dropbox-archived and template-only
            // licenses. When the PDF is rendered for the first time, it's
            // also archived to Dropbox automatically.
            const response = await licenseApi.getPdf(license.id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fallbackName = `Lizenz_${license.license_number || license.id}.pdf`;
            a.download = (license.dropbox_path && license.dropbox_path.split('/').pop()) || fallbackName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Lizenz-PDF heruntergeladen');
        } catch (err) {
            toast.error(await extractApiError(err, 'Fehler beim Herunterladen der Lizenz'));
        } finally {
            setDownloading(false);
        }
    };

    const isExpired = (() => {
        try {
            const d = license.valid_until;
            if (!d) return false;
            let date;
            if (d.includes('.')) {
                const parts = d.split('.');
                date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            } else {
                date = new Date(d);
            }
            return date < new Date();
        } catch { return false; }
    })();

    const isPendingVerification = license.source === 'self_uploaded' && !license.verified_by_admin;
    const isExternal = !!license.is_external_license;
    const conversionRequested = !!license.bgpr_conversion_requested_at;
    const [requestingConversion, setRequestingConversion] = useState(false);
    const [proofs, setProofs] = useState([]);
    const [downloadingProofId, setDownloadingProofId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        licenseApi.listProofs(license.id)
            .then((res) => { if (!cancelled) setProofs(res.data || []); })
            .catch(() => { /* still Fehlerbehandlung — Portal soll nicht crashen */ });
        return () => { cancelled = true; };
    }, [license.id]);

    const handleProofDownload = async (p) => {
        setDownloadingProofId(p.id);
        try {
            const res = await licenseApi.downloadProof(license.id, p.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = p.filename || `Nachweis_${p.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            toast.error(await extractApiError(err, 'Nachweis konnte nicht geladen werden'));
        } finally {
            setDownloadingProofId(null);
        }
    };

    const handleRequestConversion = async () => {
        const note = window.prompt(
            'Sie möchten die Umschreibung Ihrer Fremdlizenz auf eine BGPR-Lizenz beantragen?\n\n'
            + 'Die Geschäftsstelle wird Ihren Antrag prüfen und sich bei Ihnen melden.\n\n'
            + 'Optionale Anmerkung für die Geschäftsstelle:',
            ''
        );
        if (note === null) return;
        setRequestingConversion(true);
        try {
            await api.post(`/licenses/${license.id}/request-bgpr-conversion`, { note });
            toast.success('Umschreibungs-Antrag eingereicht – die Geschäftsstelle meldet sich.');
        } catch (err) {
            toast.error(await extractApiError(err, 'Antrag konnte nicht eingereicht werden'));
        } finally {
            setRequestingConversion(false);
        }
    };

    return (
        <Card className="hover:shadow-md transition-shadow" data-testid={`license-card-${license.id}`}>
            <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{license.license_type}</h4>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                isExpired ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                            }`}>
                                {isExpired ? 'Abgelaufen' : 'Gültig'}
                            </span>
                            {isPendingVerification && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700" title="Zur Prüfung eingereicht – die Geschäftsstelle prüft die Daten">
                                    In Prüfung
                                </span>
                            )}
                            {isExternal && (
                                <span
                                    className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300"
                                    title={`Diese Lizenz wurde nicht von BGPR ausgestellt${license.external_issuer ? ` (Aussteller: ${license.external_issuer})` : ''} und kann durch BGPR nicht verlängert werden.`}
                                    data-testid={`external-license-badge-${license.id}`}
                                >
                                    Fremdlizenz
                                </span>
                            )}
                            {conversionRequested && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" title="Umschreibungs-Antrag eingereicht">
                                    Umschreibung beantragt
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                            {license.license_number && (
                                <span className="flex items-center gap-1">
                                    <FileText className="h-3.5 w-3.5" />
                                    Nr. {license.license_number}
                                </span>
                            )}
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                Gültig bis: {formatDate(license.valid_until)}
                            </span>
                            {license.issued_by && (
                                <span className="flex items-center gap-1">
                                    <Shield className="h-3.5 w-3.5" />
                                    Ausgestellt von: {license.issued_by}
                                </span>
                            )}
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownload}
                        disabled={downloading}
                        className="whitespace-nowrap"
                        data-testid={`download-license-${license.id}`}
                    >
                        <Download className="h-4 w-4 mr-1" />
                        {downloading ? 'Lädt...' : 'PDF herunterladen'}
                    </Button>
                </div>
                {isExternal && (
                    <div className="mt-4 pt-3 border-t border-amber-200" data-testid={`external-info-${license.id}`}>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-2">
                            <p className="flex items-start gap-1.5">
                                <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    <strong>Hinweis:</strong> Diese Lizenz wurde nicht von der BGPR ausgestellt
                                    {license.external_issuer ? <> (Aussteller: <strong>{license.external_issuer}</strong>)</> : null}.
                                    Eine Verlängerung kann nur durch die ausstellende Einrichtung erfolgen.
                                </span>
                            </p>
                            {!conversionRequested ? (
                                <>
                                    <p>
                                        Sie können bei der BGPR die Umschreibung auf eine BGPR-Lizenz beantragen.
                                        Nach Genehmigung können wir die Verlängerung künftig direkt für Sie übernehmen.
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleRequestConversion}
                                        disabled={requestingConversion}
                                        className="border-amber-400 text-amber-900 hover:bg-amber-100"
                                        data-testid={`request-conversion-${license.id}`}
                                    >
                                        <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
                                        {requestingConversion ? 'Wird gesendet …' : 'Auf BGPR-Lizenz umschreiben beantragen'}
                                    </Button>
                                </>
                            ) : (
                                <p className="italic">
                                    Ihr Umschreibungs-Antrag ist bei der Geschäftsstelle eingegangen. Sie erhalten eine Rückmeldung, sobald die Prüfung abgeschlossen ist.
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {proofs.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-blue-100" data-testid={`license-proofs-${license.id}`}>
                        <p className="text-xs font-semibold text-blue-900 flex items-center gap-1.5 mb-2">
                            <Paperclip className="h-3.5 w-3.5" />
                            Ausbildungsnachweise
                        </p>
                        <ul className="space-y-1">
                            {proofs.map((p) => (
                                <li key={p.id} className="flex items-center justify-between gap-2 text-xs">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <FileText className="h-3.5 w-3.5 text-blue-700 shrink-0" />
                                        <span className="truncate">
                                            <span className="font-medium">{p.filename}</span>
                                            {p.note && <span className="text-gray-500 italic"> — {p.note}</span>}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleProofDownload(p)}
                                        disabled={downloadingProofId === p.id}
                                        className="text-blue-700 hover:text-blue-900 underline decoration-dotted underline-offset-2 shrink-0 disabled:opacity-50"
                                        data-testid={`portal-download-proof-${p.id}`}
                                    >
                                        {downloadingProofId === p.id ? 'Lädt…' : 'Anzeigen'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {(Array.isArray(license.consumed_training_records) && license.consumed_training_records.length > 0)
                    || (renewal && Array.isArray(renewal.consumed_training_records) && renewal.consumed_training_records.length > 0) ? (() => {
                    const records = (Array.isArray(license.consumed_training_records) && license.consumed_training_records.length)
                        ? license.consumed_training_records
                        : renewal.consumed_training_records;
                    const total = license.total_consumed_ue != null
                        ? license.total_consumed_ue
                        : (renewal?.total_consumed_ue != null
                            ? renewal.total_consumed_ue
                            : records.reduce((s, c) => s + (c.ue || 0), 0));
                    return (
                    <div className="mt-4 pt-3 border-t border-blue-100"
                         data-testid={`license-ue-basis-${license.id}`}>
                        <p className="text-xs font-semibold text-blue-900 flex items-center gap-1.5 mb-2">
                            <Award className="h-3.5 w-3.5" />
                            UE-Basis dieser Verlängerung
                            <span className="text-blue-700 font-bold">({total} UE gesamt)</span>
                        </p>
                        <ul className="space-y-1">
                            {records.map((c, idx) => (
                                <li key={c.id || idx} className="text-xs text-blue-900/90 flex items-center justify-between gap-2">
                                    <span className="truncate">
                                        <span className="font-medium">{c.name || 'Nachweis'}</span>
                                        {c.date_issued && <span className="text-blue-900/60"> – {formatDate(c.date_issued)}</span>}
                                    </span>
                                    <span className="font-semibold shrink-0">{c.ue || 0} UE</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-gray-500 mt-2">
                            Diese Fortbildungen wurden für Ihre aktuelle Lizenzperiode angerechnet und stehen für weitere Verlängerungen nicht mehr zur Verfügung.
                        </p>
                    </div>
                    );
                })() : null}
            </CardContent>
        </Card>
    );
}

export default LicenseCard;
